// ── Tracking event types ─────────────────────────────────────────────────────
// The typed vocabulary for every event this SPA may hand to the data layer. Pure types plus two
// constants — nothing here reads the window, the session or GTM — so the consent gate, the
// dispatcher and the tests can all import it without pulling a runtime dependency along.
//
// The union below is the contract. A parameter that an event does not declare is typed `never` on
// that event, so putting `value` on a generate_lead is a compile error rather than a privacy
// incident discovered in the GA4 UI six weeks later.

/**
 * Bumped whenever an event's parameter set changes meaning. It rides on every event so a report can
 * tell which contract produced a row, instead of reinterpreting old rows under new rules.
 */
export const TRACKING_SCHEMA_VERSION = "1.0.0";

// ── Context value domains ────────────────────────────────────────────────────

/**
 * Which of the three audiences the actor belongs to.
 * <p>A = internal (sales rep on a <c>?token=</c> deep link, or Admin/GarageInternal permission),
 * B = dealer, C = end customer. Mapped from <c>SessionContext.principal</c> plus permissions;
 * see shared/stores/sessionStore. Group A is company traffic and is never forwarded to Ads, Meta
 * or TikTok — it would train bidding on our own staff.</p>
 */
export type UserGroup = "A" | "B" | "C";

/** Whether the browser carries a signed-in identity (<c>SessionContext.userId != null</c>). */
export type AuthState = "authenticated" | "guest";

/** Deployment the event came from. Keeps test traffic out of the production reports. */
export type TrackingEnvironment = "development" | "test" | "production";

/**
 * Whether the app is wearing the LOBAS brand or a partner's. Derived from the tenant resolved
 * server-side by <c>GET /api/tenant/config</c> (host-based), never from a query parameter.
 */
export type BrandMode = "lobas" | "whitelabel";

/** The funnel an event belongs to. Lets one report separate the lead path from the webshop path. */
export type BusinessFlow =
  | "catalog"
  | "configurator"
  | "lead"
  | "webshop_checkout"
  | "internal_order"
  | "account"
  | "dashboard";

/** Where the event was raised. Server-side events carry their own source so they can be told apart. */
export type EventSource = "configurator_spa" | "backend_integration";

/** Prices in this app are Norwegian only; a second currency would be a schema change, not a value. */
export type Currency = "NOK";

/** Wizard step ids — mirrors WIZARD_STEPS in modules/garage/store/wizard.ts. */
export type ConfiguratorStep =
  | "welcome"
  | "dimensions"
  | "panel"
  | "fittings"
  | "accessories"
  | "opener"
  | "options"
  | "viewGarage"
  | "exteriorDoors"
  | "partial"
  | "summary";

/** How an account was authenticated or created. */
export type AuthMethod = "b2c" | "email" | "sales_token";

/** Where a lead originated. A closed set — never a free-text campaign string. */
export type LeadSource = "configurator" | "dealer_request" | "webshop";

/** Dashboard sort columns — mirrors SortField in modules/home/pages/HomePage.tsx. */
export type DashboardSortField =
  | "lastModifiedAt"
  | "name"
  | "createdAt"
  | "status"
  | "customer"
  | "favorite";

export type SortDirection = "asc" | "desc";

/** Dashboard stat filters — mirrors StatFilter in modules/home/pages/HomePage.tsx ("all" = no filter). */
export type DashboardFilter = "all" | "active" | "ordered" | "favorites";

// ── Ecommerce item ───────────────────────────────────────────────────────────

/**
 * One line in an ecommerce event. Every field is product metadata: an item name is a door model
 * name from the catalogue, never anything a customer typed. The sanitiser scans these values too,
 * so a name that ever came from a free-text field is dropped rather than shipped.
 */
export interface TrackingItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  item_variant?: string;
  item_brand?: string;
  price?: number;
  quantity?: number;
  index?: number;
}

// ── Envelope ─────────────────────────────────────────────────────────────────

/**
 * The fields every event carries, whatever it is. They describe the actor and the deployment, never
 * the person: no user id, no e-mail, no dealer contact — <c>dealer_id</c> is the ERP account number
 * of a company, which is not personal data.
 */
export interface EventContext {
  /** UUID minted per event. The deduplication key when the same event reaches two destinations. */
  event_id: string;
  /** Value of TRACKING_SCHEMA_VERSION at the time the event was built. */
  tracking_schema_version: string;
  /** ISO 8601 with a UTC offset, e.g. "2026-08-27T09:41:12.004Z". */
  event_timestamp: string;
  environment: TrackingEnvironment;
  /** Build identity (package version + commit), so a reporting anomaly can be tied to a release. */
  app_version: string;
  /** Tenant id from GET /api/tenant/config — "default"/"xlbygg". Host-resolved, server-side. */
  tenant_id: string;
  brand_mode: BrandMode;
  user_group: UserGroup;
  auth_state: AuthState;
  business_flow: BusinessFlow;
  event_source: EventSource;
  /** Which consent text the visitor actually accepted, so a consent record can be reconciled. */
  consent_policy_version: string;
  /**
   * The store a `?dealer=` link locked this tab to: a D365 customer number, one to twelve digits (see
   * shared/lockedDealer). Left out entirely when there is no lock — never "" and never null. A chain's
   * GTM container reads it to send the lead to that store's own Ads account and to split its GA4 by
   * store, so its name and format do not change without telling whoever runs the tracking.
   */
  locked_dealer_id?: string;
}

// ── Parameters ───────────────────────────────────────────────────────────────

/**
 * Every parameter any event may carry — the closed universe the union draws from. A parameter that
 * is not declared here cannot be added to an event without editing this file, which is the point:
 * the review that adds a field is the review that decides whether it may leave the browser.
 */
export interface TrackingParams {
  /** Absolute URL, already run through sanitizeUrl (no fragment, only allow-listed query params). */
  page_location: string;
  /** Document title, already run through sanitizeText. */
  page_title: string;
  /** Referrer, already run through sanitizeReferrer (foreign origins keep origin + path only). */
  page_referrer: string;

  /** The configuration (project) the event is about. An internal id, never a project name. */
  configuration_id: string;
  /** Save revision of that configuration, so a resumed job can be told from a fresh one. */
  configuration_version: number;
  configurator_step: ConfiguratorStep;
  /** Zero-based position of the step in WIZARD_STEPS — lets a funnel be ordered without a lookup. */
  configurator_step_index: number;
  /** Option group id (e.g. "panelDesign"). A stable key from the catalogue, not a label. */
  option_group: string;
  /** Option id within the group. A stable key from the catalogue, not a label. */
  option_id: string;
  /** Machine-readable error key. Never the message — a message can echo what the user typed. */
  error_code: string;
  /** Catalogue id of the door model being configured. */
  door_model_id: string;

  /** ERP account number of a dealer company. Not personal data, and never a contact person. */
  dealer_id: string;
  /** How many dealers a list showed, or a request was sent to. */
  dealer_count: number;

  /** Master lead id — the quotationNumber from POST /api/garage/quotation/request-dealers. */
  lead_id: string;
  lead_source: LeadSource;
  /** Closed-set code for why a lead was dropped. Never a free-text reason. */
  reason_code: string;

  /** The product kinds an order holds — "garageDoor", "exteriorDoor", "upAndOverDoor", "garagePartial" —
   *  sorted and joined with "|". A closed vocabulary, never a product name. */
  product_kinds: string;
  /** How many products the order holds: doors of a row plus the exterior doors beside them. */
  item_count: number;

  /** Webshop handoff id — engineReference (intentId) from POST /api/garage/order. */
  handoff_id: string;
  /** Order id. For purchase this is the quotationNumber the webshop confirms back to us. */
  transaction_id: string;
  currency: Currency;
  value: number;
  items: readonly TrackingItem[];
  item_list_id: string;
  item_list_name: string;

  method: AuthMethod;

  /** How many projects the dashboard listed. */
  project_count: number;
  /** Length of the search term. The term itself is a free-text field and never leaves the browser. */
  search_length: number;
  filter_key: DashboardFilter;
  sort_field: DashboardSortField;
  sort_direction: SortDirection;
}

/** Every legal parameter name. */
export type ParamName = keyof TrackingParams;

// ── Event union ──────────────────────────────────────────────────────────────

/**
 * One event: the envelope, the discriminator, the required parameters, the optional ones — and
 * every remaining parameter pinned to <c>never</c>.
 * <p>That last intersection is what makes a wrong field a type error. Without it TypeScript's
 * excess-property check would accept <c>value</c> on a generate_lead simply because some other
 * member of the union declares <c>value</c>, and the rule "generate_lead carries no value and no
 * currency" would live only in a document.</p>
 */
type TrackedEvent<E extends string, Req extends ParamName = never, Opt extends ParamName = never> =
  EventContext &
  { event: E } &
  Pick<TrackingParams, Req> &
  Partial<Pick<TrackingParams, Opt>> &
  Partial<Record<Exclude<ParamName, Req | Opt>, never>>;

// Page and catalogue
export type PageViewEvent = TrackedEvent<"page_view", "page_location" | "page_title", "page_referrer">;
export type ViewItemListEvent = TrackedEvent<"view_item_list", "item_list_id" | "item_list_name" | "items">;
export type SelectItemEvent = TrackedEvent<"select_item", "items", "item_list_id" | "item_list_name">;
export type ViewItemEvent = TrackedEvent<"view_item", "items", "door_model_id" | "value" | "currency">;

// Configurator
export type ConfiguratorStartEvent =
  TrackedEvent<"configurator_start", "configuration_id", "configuration_version" | "door_model_id">;
export type ConfiguratorStepViewEvent =
  TrackedEvent<"configurator_step_view", "configuration_id" | "configurator_step", "configurator_step_index">;
export type ConfiguratorStepCompleteEvent =
  TrackedEvent<"configurator_step_complete", "configuration_id" | "configurator_step", "configurator_step_index">;
export type ConfiguratorOptionSelectEvent =
  TrackedEvent<"configurator_option_select",
    "configuration_id" | "configurator_step" | "option_group" | "option_id", "door_model_id">;
export type ConfiguratorValidationErrorEvent =
  TrackedEvent<"configurator_validation_error",
    "configuration_id" | "configurator_step" | "error_code", "option_group">;
export type ConfiguratorSaveEvent =
  TrackedEvent<"configurator_save", "configuration_id",
    "configuration_version" | "door_model_id" | "product_kinds" | "item_count">;
export type ConfiguratorResumeEvent =
  TrackedEvent<"configurator_resume", "configuration_id", "configuration_version">;
export type ConfiguratorCompleteEvent =
  TrackedEvent<"configurator_complete", "configuration_id",
    "configuration_version" | "door_model_id" | "value" | "currency" | "items" | "product_kinds" | "item_count">;

// Account
export type SignUpEvent = TrackedEvent<"sign_up", "method">;
export type LoginEvent = TrackedEvent<"login", "method">;
export type LogoutEvent = TrackedEvent<"logout">;

// Dealer selection and lead
export type DealerListViewEvent = TrackedEvent<"dealer_list_view", "dealer_count", "configuration_id">;
export type DealerSelectEvent = TrackedEvent<"dealer_select", "dealer_id", "configuration_id">;
export type QuoteRequestStartEvent =
  TrackedEvent<"quote_request_start", "configuration_id",
    "dealer_count" | "dealer_id" | "product_kinds" | "item_count">;
/**
 * The lead itself. One event per request no matter how many dealers received a copy — the master id
 * is the quotationNumber, and <c>dealer_count</c> carries the fan-out.
 * <p>No <c>value</c> and no <c>currency</c>: a quotation request is not revenue, and pricing a lead
 * would feed a bid strategy a number no one has agreed to pay. Both are typed <c>never</c> here.</p>
 * <p><c>dealer_id</c> only when <c>dealer_count</c> is 1: the one dealer it went to.</p>
 */
export type GenerateLeadEvent =
  TrackedEvent<"generate_lead", "lead_id" | "dealer_count",
    "configuration_id" | "dealer_id" | "lead_source" | "product_kinds" | "item_count">;

// Lead lifecycle — raised server-side by the status transitions, not by the browser. There is no
// CRM; a lead is worked, qualified and closed in the portal itself.
export type QualifyLeadEvent = TrackedEvent<"qualify_lead", "lead_id", "dealer_id" | "lead_source">;
export type WorkingLeadEvent = TrackedEvent<"working_lead", "lead_id", "dealer_id" | "lead_source">;
export type DisqualifyLeadEvent = TrackedEvent<"disqualify_lead", "lead_id", "dealer_id" | "reason_code">;
export type CloseConvertLeadEvent =
  TrackedEvent<"close_convert_lead", "lead_id", "dealer_id" | "value" | "currency" | "transaction_id">;
export type CloseUnconvertLeadEvent =
  TrackedEvent<"close_unconvert_lead", "lead_id", "dealer_id" | "reason_code">;

// Checkout and order
/**
 * The webshop handoff. Raised only once the handoff exists — a <c>resumedExistingCart</c> response
 * reopened a basket and created nothing, so it must not produce this event.
 */
export type BeginCheckoutEvent =
  TrackedEvent<"begin_checkout", "handoff_id" | "configuration_id",
    "value" | "currency" | "items" | "product_kinds" | "item_count">;
export type CheckoutHandoffErrorEvent =
  TrackedEvent<"checkout_handoff_error", "error_code", "configuration_id" | "handoff_id">;
/**
 * The order. Confirmed server-side by POST /api/integration/orders/confirmed, which is the only
 * place the AddedToCart → OrderPlaced transition is observed exactly once; a retry of that callback
 * updates nothing and must not raise a second purchase.
 */
export type PurchaseEvent =
  TrackedEvent<"purchase", "transaction_id",
    "value" | "currency" | "items" | "lead_id" | "configuration_id">;
export type RefundEvent = TrackedEvent<"refund", "transaction_id", "value" | "currency" | "items">;

// Dashboard
export type DashboardViewEvent = TrackedEvent<"dashboard_view", never, "project_count" | "filter_key">;
export type DashboardSearchEvent = TrackedEvent<"dashboard_search", "search_length">;
export type DashboardFilterEvent = TrackedEvent<"dashboard_filter", "filter_key", "project_count">;
export type DashboardSortEvent = TrackedEvent<"dashboard_sort", "sort_field" | "sort_direction">;
export type DashboardProjectOpenEvent = TrackedEvent<"dashboard_project_open", "configuration_id">;
export type DashboardProjectCreateEvent = TrackedEvent<"dashboard_project_create", never, "configuration_id">;
export type DashboardProjectDuplicateEvent = TrackedEvent<"dashboard_project_duplicate", "configuration_id">;
export type DashboardProjectRenameEvent = TrackedEvent<"dashboard_project_rename", "configuration_id">;
export type DashboardProjectArchiveEvent = TrackedEvent<"dashboard_project_archive", "configuration_id">;
export type DashboardProjectFavoriteEvent = TrackedEvent<"dashboard_project_favorite", "configuration_id">;
export type DashboardProjectDeleteEvent = TrackedEvent<"dashboard_project_delete", "configuration_id">;

/** Every event the app is allowed to raise. */
export type TrackingEvent =
  | PageViewEvent
  | ViewItemListEvent
  | SelectItemEvent
  | ViewItemEvent
  | ConfiguratorStartEvent
  | ConfiguratorStepViewEvent
  | ConfiguratorStepCompleteEvent
  | ConfiguratorOptionSelectEvent
  | ConfiguratorValidationErrorEvent
  | ConfiguratorSaveEvent
  | ConfiguratorResumeEvent
  | ConfiguratorCompleteEvent
  | SignUpEvent
  | LoginEvent
  | LogoutEvent
  | DealerListViewEvent
  | DealerSelectEvent
  | QuoteRequestStartEvent
  | GenerateLeadEvent
  | QualifyLeadEvent
  | WorkingLeadEvent
  | DisqualifyLeadEvent
  | CloseConvertLeadEvent
  | CloseUnconvertLeadEvent
  | BeginCheckoutEvent
  | CheckoutHandoffErrorEvent
  | PurchaseEvent
  | RefundEvent
  | DashboardViewEvent
  | DashboardSearchEvent
  | DashboardFilterEvent
  | DashboardSortEvent
  | DashboardProjectOpenEvent
  | DashboardProjectCreateEvent
  | DashboardProjectDuplicateEvent
  | DashboardProjectRenameEvent
  | DashboardProjectArchiveEvent
  | DashboardProjectFavoriteEvent
  | DashboardProjectDeleteEvent;

/** Every legal event name. */
export type EventName = TrackingEvent["event"];

/**
 * Narrows the union to one member.
 * <p>Pass an object literal to a parameter typed <c>EventOf<"generate_lead"></c> and TypeScript runs
 * a real excess-property check against that one shape; pass it to the whole union and it only checks
 * that the property exists somewhere in the union. Builders should take EventOf, not TrackingEvent.</p>
 */
export type EventOf<E extends EventName> = Extract<TrackingEvent, { event: E }>;

/**
 * The parameters an event type requires. Used by schema.ts to keep the runtime allowlist and the
 * types from drifting apart.
 */
export type RequiredParamsOf<T> = {
  [K in Extract<keyof T, ParamName>]-?: {} extends Pick<T, K> ? never : K;
}[Extract<keyof T, ParamName>];

/** The parameters an event type accepts but does not require. The `?: never` blocks drop out. */
export type OptionalParamsOf<T> = {
  [K in Extract<keyof T, ParamName>]-?: {} extends Pick<T, K>
    ? ([Exclude<T[K], undefined>] extends [never] ? never : K)
    : never;
}[Extract<keyof T, ParamName>];
